import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function PiCreatorDropdown(props: {
  trigger: React.ReactNode;
  items: JSX.Element[];
  label?: string;
  style?: "default" | "small";
  triggerChrome?: "default" | "none";
  disabled?: boolean;
}) {
  const {
    trigger,
    items,
    label,
    style = "default",
    triggerChrome = "default",
    disabled = false,
  } = { ...props };
  const [width, setWidth] = useState<number>(1000);
  const ref = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (ref.current) {
      var w = ref.current?.offsetWidth.toString() + "px";
      setWidth(ref.current?.offsetWidth);
    }
  }, [ref.current?.offsetWidth]);

  // prevent node canvas from zooming when scrolling in the dropdown
  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  const handlePointerInteraction = (
    e: React.PointerEvent | React.MouseEvent,
  ) => {
    e.stopPropagation();
  };

  const triggerView =
    triggerChrome === "none" ? (
      trigger
    ) : (
      <>
        <div
          className={
            "flex-1 my-auto h-full font-medium truncate text-creator-text text-[14px] text-interaction"
          }
        >
          {trigger}
        </div>
        <div
          className={classNames({
            "my-auto ml-auto text-creator-text": true,
            "transition duration-100 ease-in-out": true,
            "transform rotate-180": isOpen,
            "transform rotate-0": !isOpen,
          })}
        >
          <ExpandMoreIcon color={"inherit"} />
        </div>
      </>
    );

  return (
    <div
      ref={ref}
      className={"flex flex-col gap-[8px] noDrag nowheel"}
      onWheel={handleWheel}
    >
      {label && (
        <div className={"font-medium text-creator-text text-[14px]"}>
          {label}
        </div>
      )}
      <DropdownMenu.Root onOpenChange={setIsOpen}>
        <DropdownMenu.Trigger
          disabled={disabled}
          className={classNames(
            "flex w-full focus:outline-none",
            {
              "bg-creator-primary-background border-solid items-center border-creator-primary rounded-[14px] border-[2px]":
                triggerChrome === "default",
              "items-stretch": triggerChrome === "none",
              "h-[32px]  rounded-full px-[4px]": style === "small",
              "h-[68px]  px-[12px] py-[10px] ":
                style === "default" && triggerChrome === "default",
              "opacity-60 cursor-not-allowed": disabled,
            },
          )}
          onPointerDown={handlePointerInteraction}
          onPointerMove={handlePointerInteraction}
          onMouseDown={handlePointerInteraction}
        >
          {triggerView}
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            style={{ width: width + "px" }}
            className={
              "flex flex-col max-h-[200px] pit-creator-scrollbar overflow-y-auto gap-[10px] py-[10px] rounded-[5px] bg-creator-surface overflow-x-hidden" +
              " border-solid" +
              " border-[1px]" +
              " border-creator-border drop-shadow-md z-[10050]"
            }
            onWheel={handleWheel}
            onPointerDown={handlePointerInteraction}
            onPointerMove={handlePointerInteraction}
            onMouseDown={handlePointerInteraction}
            side="bottom"
            align="start"
            sideOffset={5}
            collisionPadding={8}
          >
            {items.map((item, index) => {
              return (
                <DropdownMenu.Item
                  key={index}
                  className={
                    "cursor-pointer focus:outline-none px-[8px] hover:bg-creator-primary-washed"
                  }
                  onWheel={handleWheel}
                >
                  {item}
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
